import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendLineChart, formatChartNumber } from '@heyroot/ui/reports';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { ErrorState } from '@heyroot/ui/primitives/ErrorState';
import { Select } from '@heyroot/ui/primitives/Select';
import { Skeleton } from '@heyroot/ui/primitives/Skeleton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from '@heyroot/ui/primitives/Table';
import { fetchTtvTrend, type TtvCohortRow } from '../../lib/onboarding-funnel-api.js';

const TTV_METRICS = [
  { key: 'time_to_first_item', label: 'Time to First Item' },
  { key: 'time_to_go_live', label: 'Time to Go Live' },
  { key: 'time_to_first_real_sale', label: 'Time to First Real Sale' },
  { key: 'activated_14d', label: 'Activated within 14 Days' },
] as const;

// ADR-0111 activation north-star: a proportion (shareValue), not a duration --
// it has no p50/p75 percentile toggle and renders its own stat shape below.
const ACTIVATION_METRIC = 'activated_14d';

const BUSINESS_TYPES = [
  { value: 'all', label: 'All business types' },
  { value: 'full_service_restaurant', label: 'Full-service restaurant' },
  { value: 'quick_service', label: 'Quick service' },
  { value: 'bar_nightclub', label: 'Bar / nightclub' },
  { value: 'retail_store', label: 'Retail store' },
  { value: 'hotel_hospitality', label: 'Hotel / hospitality' },
  { value: 'service_appointments', label: 'Service appointments' },
  { value: 'country_club', label: 'Country club' },
  { value: 'mixed', label: 'Mixed' },
] as const;

export function TtvTrendsPanel({
  businessTypeFilter,
  onBusinessTypeFilterChange,
}: {
  businessTypeFilter: string | 'all';
  onBusinessTypeFilterChange: (v: string | 'all') => void;
}) {
  const [metric, setMetric] = useState<(typeof TTV_METRICS)[number]['key']>('time_to_go_live');
  const [percentile, setPercentile] = useState<'p50Days' | 'p75Days'>('p50Days');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['onboarding-funnel-ttv', metric],
    queryFn: () => fetchTtvTrend(metric),
  });

  if (isLoading) return <Skeleton data-testid="ttv-trends-skeleton" />;
  if (isError) {
    return <ErrorState description="Couldn't load TTV trends." onRetry={() => void refetch()} />;
  }

  const cohorts = data?.cohorts ?? [];
  const filtered =
    businessTypeFilter === 'all'
      ? cohorts
      : cohorts.filter((c) => c.businessType === businessTypeFilter);
  const last6 = [...filtered].sort((a, b) => a.cohortMonth.localeCompare(b.cohortMonth)).slice(-6);

  const metricLabel = TTV_METRICS.find((m) => m.key === metric)!.label;
  const freshAt = filtered[0]?.computedAt;
  const isActivation = metric === ACTIVATION_METRIC;
  const firstVal = isActivation ? (last6[0]?.shareValue ?? null) : (last6[0]?.[percentile] ?? null);
  const lastVal = isActivation
    ? (last6[last6.length - 1]?.shareValue ?? null)
    : (last6[last6.length - 1]?.[percentile] ?? null);
  const formatShare = (v: number) => `${Math.round(v * 100)}%`;
  // Singular/plural follows the printed number (1.004 prints "1"), and a non-finite value stays blank.
  const formatDays = (v: number) => {
    const text = formatChartNumber(v);
    return text === '' ? '' : `${text} ${text === '1' ? 'day' : 'days'}`;
  };

  // The metric/business-type/percentile controls stay visible even when the
  // current selection has no data -- otherwise a user landing on an empty
  // metric would have no way to switch to one that has data.
  return (
    <div className="flex flex-col gap-4">
      <div role="tablist" aria-label="TTV metric">
        {TTV_METRICS.map((m) => (
          <button
            key={m.key}
            role="tab"
            aria-selected={metric === m.key}
            onClick={() => setMetric(m.key)}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Form-vs-tool (spec §4 locked decision, "a tool, not a bare <select>"):
          business type is a long/dynamic list (free-text column, no fixed DB
          enum) -- @heyroot/ui's real, design-system-styled `Select` primitive
          is the sanctioned list-picker for that shape, not a tab bar. */}
      <label htmlFor="ttv-business-type-filter" className="flex flex-col gap-1.5">
        <span className="text-body-sm text-secondary">Business type</span>
        <Select
          id="ttv-business-type-filter"
          aria-label="Business type"
          value={businessTypeFilter}
          onChange={(e) => onBusinessTypeFilterChange(e.target.value)}
        >
          {BUSINESS_TYPES.map((bt) => (
            <option key={bt.value} value={bt.value}>
              {bt.label}
            </option>
          ))}
        </Select>
      </label>

      {/* Percentile is a fixed 2-option exclusive choice -- same shape as the
          metric selector above, so it reuses that exact same hand-rolled
          role="tablist"/role="tab" pattern rather than a third control type.
          activated_14d is a proportion, not a duration -- it has no percentile
          to toggle, so the control is hidden rather than rendered inert. */}
      {!isActivation && (
        <div role="tablist" aria-label="Percentile">
          {(['p50Days', 'p75Days'] as const).map((p) => (
            <button
              key={p}
              role="tab"
              aria-selected={percentile === p}
              onClick={() => setPercentile(p)}
            >
              {p === 'p50Days' ? 'p50' : 'p75'}
            </button>
          ))}
        </div>
      )}

      {last6.length === 0 ? (
        <EmptyState
          title="No cohort data yet for this metric."
          description="Cohorts are computed live; one appears here once enough tenants in a signup month have completed this step."
        />
      ) : (
        <>
          <p>
            {firstVal != null && lastVal != null
              ? isActivation
                ? `${metricLabel} moved from ${formatShare(firstVal)} to ${formatShare(lastVal)} over the last ${last6.length} cohorts.`
                : `${metricLabel} ${percentile === 'p50Days' ? 'median' : 'p75'} moved from ${firstVal} to ${lastVal} days over the last ${last6.length} cohorts.`
              : `${metricLabel} has insufficient sample size for a trend line in some cohorts.`}
          </p>
          {freshAt && (
            <p className="text-body-sm text-muted">
              Computed as of {new Date(freshAt).toISOString()}
            </p>
          )}

          <div data-testid="ttv-trend-chart">
            {/* TrendLineChart's generic constrains TRow to Record<string, unknown>,
                which a closed interface like TtvCohortRow does not structurally
                satisfy without an index signature (same repo-documented quirk
                VolumeBarChart hit elsewhere) -- the intersection cast is safe
                since the constraint is purely a TS structural-typing artifact,
                not a real runtime shape difference. */}
            <TrendLineChart<TtvCohortRow & Record<string, unknown>>
              data={last6 as (TtvCohortRow & Record<string, unknown>)[]}
              xKey="cohortMonth"
              yKey={isActivation ? 'shareValue' : percentile}
              metricClass={isActivation ? 'utilization' : 'time'}
              variant="line"
              name={isActivation ? metricLabel : percentile === 'p50Days' ? 'Median' : 'p75'}
              valueFormatter={isActivation ? formatShare : formatDays}
            />
          </div>

          <Table aria-label="Exact cohort values">
            <TableHead>
              <TableRow>
                <TableHeaderCell>Cohort month</TableHeaderCell>
                <TableHeaderCell>n</TableHeaderCell>
                {isActivation ? (
                  <TableHeaderCell>Activated within 14d</TableHeaderCell>
                ) : (
                  <>
                    <TableHeaderCell>p50 days</TableHeaderCell>
                    <TableHeaderCell>p75 days</TableHeaderCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {last6.map((c) => (
                <TableRow key={c.cohortMonth}>
                  <TableCell>{c.cohortMonth}</TableCell>
                  <TableCell>
                    {c.n} {c.n < 5 && <Badge variant="warning">small sample</Badge>}
                  </TableCell>
                  {isActivation ? (
                    <TableCell>{c.shareValue != null ? formatShare(c.shareValue) : '—'}</TableCell>
                  ) : (
                    <>
                      <TableCell>{c.p50Days ?? '—'}</TableCell>
                      <TableCell>{c.p75Days ?? '—'}</TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </div>
  );
}
